import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Calendar, Package, Edit, Trash2, Save, X, BarChart3, FileDown, Upload, TrendingUp, Image as ImageIcon, MoveUp, MoveDown, Lock, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import AdminPasswordChange from '../components/AdminPasswordChange';
import ImageUpload from '../components/ImageUpload';
import UserManagement from '../components/UserManagement';
import AddServiceForm from '../components/AddServiceForm';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [userRole, setUserRole] = useState('support'); // NEW: Track user role
  const [showPasswordModal, setShowPasswordModal] = useState(false); // NEW: Password change modal
  const [showAddServiceModal, setShowAddServiceModal] = useState(false); // NEW: Add service modal
  
  // Appointment filters
  const [appointmentDateFilter, setAppointmentDateFilter] = useState('');
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState('all');
  
  // Report filters
  const [reportFilter, setReportFilter] = useState({
    type: 'all',
    startDate: '',
    endDate: ''
  });
  
  // Analytics filters
  const [analyticsFilter, setAnalyticsFilter] = useState({
    type: 'all',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Gallery upload
  const [newGalleryImage, setNewGalleryImage] = useState({
    image_url: '',
    title: '',
    category: 'general',
    order: 0,
    group_link: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    // Decode token to get role
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserRole(payload.role || 'support');
    } catch (e) {
      console.error('Error decoding token:', e);
    }
    
    fetchData();
  }, [activeTab, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (activeTab === 'appointments') {
        const response = await axios.get(`${API}/appointments`, { headers });
        setAppointments(response.data);
      } else if (activeTab === 'services') {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } else if (activeTab === 'analytics') {
        fetchAnalytics();
      } else if (activeTab === 'gallery') {
        const response = await axios.get(`${API}/gallery`);
        setGalleryImages(response.data.sort((a, b) => a.order - b.order));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams({
        filter_type: analyticsFilter.type,
        ...(analyticsFilter.type !== 'all' && { date: analyticsFilter.date })
      });
      
      const response = await axios.get(`${API}/analytics/service-usage?${params}`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/appointments/${appointmentId}`, { status }, { headers });
      toast.success('Appointment status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const getFilteredAppointments = () => {
    let filtered = [...appointments];
    
    // Filter by date
    if (appointmentDateFilter) {
      filtered = filtered.filter(app => app.appointment_date === appointmentDateFilter);
    }
    
    // Filter by status
    if (appointmentStatusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === appointmentStatusFilter);
    }
    
    // Sort by date (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.appointment_date);
      const dateB = new Date(b.appointment_date);
      return dateB - dateA;
    });
    
    return filtered;
  };

  const handleEditService = (service) => {
    setEditingService(service.id);
    setEditForm({
      name: service.name,
      category: service.category,
      price: service.price,
      duration: service.duration,
      is_trending: service.is_trending || false
    });
  };

  const handleSaveService = async (serviceId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/services/${serviceId}`, editForm, { headers });
      toast.success('Service updated successfully');
      setEditingService(null);
      fetchData();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/services/${serviceId}`, { headers });
      toast.success('Service deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const downloadCSVReport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams({
        filter_type: reportFilter.type,
        ...(reportFilter.type === 'day' && reportFilter.startDate && { start_date: reportFilter.startDate }),
        ...(reportFilter.type === 'month' && reportFilter.startDate && { start_date: reportFilter.startDate }),
        ...(reportFilter.type === 'year' && reportFilter.startDate && { start_date: reportFilter.startDate }),
        ...(reportFilter.type === 'custom' && reportFilter.startDate && { start_date: reportFilter.startDate }),
        ...(reportFilter.type === 'custom' && reportFilter.endDate && { end_date: reportFilter.endDate })
      });
      
      const response = await axios.get(`${API}/reports/appointments/csv?${params}`, { 
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointments_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const syncToGoogleSheets = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/reports/sync-to-sheets`, {}, { headers });
      
      if (response.data.status === 'pending') {
        toast.info(response.data.message);
      } else {
        toast.success('Data synced to Google Sheets successfully');
      }
    } catch (error) {
      console.error('Error syncing to sheets:', error);
      toast.error('Failed to sync to Google Sheets');
    }
  };

  const handleAddGalleryImage = async () => {
    if (!newGalleryImage.image_url) {
      toast.error('Please enter an image URL');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/gallery`, newGalleryImage, { headers });
      toast.success('Image added successfully');
      setNewGalleryImage({ image_url: '', title: '', category: 'general', order: 0, group_link: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Failed to add image');
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/gallery/${imageId}`, { headers });
      toast.success('Image deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleUpdateImageOrder = async (imageId, newOrder) => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/gallery/${imageId}`, { order: newOrder }, { headers });
      toast.success('Order updated');
      fetchData();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const getChartData = () => {
    if (!analytics || !analytics.most_popular) return null;
    
    const topServices = analytics.most_popular.slice(0, 10);
    
    return {
      labels: topServices.map(s => s.service_name.substring(0, 30)),
      datasets: [
        {
          label: 'Usage Count',
          data: topServices.map(s => s.usage_count),
          backgroundColor: 'rgba(212, 165, 116, 0.8)',
          borderColor: 'rgba(212, 165, 116, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #f0f0f0',
        padding: '1.5rem 2rem',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Link to="/" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textDecoration: 'none'
            }}>
              <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '100px', width: 'auto' }} />
            </Link>
            <span style={{ marginLeft: '1rem', color: '#5a6c7d' }}>Admin Dashboard</span>
            <span style={{
              marginLeft: '1rem',
              padding: '0.25rem 0.75rem',
              background: userRole === 'primary' ? 'linear-gradient(135deg, #d4a574, #c89b64)' : '#f0f0f0',
              color: userRole === 'primary' ? 'white' : '#5a6c7d',
              borderRadius: '50px',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {userRole === 'primary' ? 'Primary Admin' : 'Support Admin'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowPasswordModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'white',
                border: '2px solid #f0f0f0',
                borderRadius: '50px',
                color: '#2c3e50',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Lock size={18} />
              Change Password
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'white',
                border: '2px solid #f0f0f0',
                borderRadius: '50px',
                color: '#2c3e50',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #f0f0f0',
        padding: '0 2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          gap: '2rem'
        }}>
          {[
            { id: 'appointments', icon: Calendar, label: 'Appointments' },
            { id: 'services', icon: Package, label: 'Services' },
            { id: 'reports', icon: FileDown, label: 'Reports' },
            { id: 'analytics', icon: BarChart3, label: 'Analytics' },
            { id: 'gallery', icon: ImageIcon, label: 'Gallery' },
            ...(userRole === 'primary' ? [{ id: 'users', icon: Users, label: 'User Management' }] : [])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                padding: '1rem 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #d4a574' : '3px solid transparent',
                color: activeTab === tab.id ? '#d4a574' : '#5a6c7d',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <tab.icon size={20} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  Appointments Management
                </h2>
                
                {/* Date Filter */}
                <div style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '15px',
                  marginBottom: '1.5rem',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.5rem', display: 'block' }}>
                        Filter by Date:
                      </label>
                      <input
                        type="date"
                        value={appointmentDateFilter}
                        onChange={(e) => setAppointmentDateFilter(e.target.value)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '2px solid #d4a574',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <button
                      onClick={() => setAppointmentDateFilter('')}
                      style={{
                        marginTop: '1.5rem',
                        padding: '0.5rem 1rem',
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>
                
                {/* Status Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginBottom: '1.5rem',
                  overflowX: 'auto',
                  paddingBottom: '0.5rem'
                }}>
                  {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setAppointmentStatusFilter(status)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '10px',
                        border: 'none',
                        background: appointmentStatusFilter === status
                          ? 'linear-gradient(135deg, #d4a574, #c89b64)'
                          : 'white',
                        color: appointmentStatusFilter === status ? 'white' : '#2c3e50',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap',
                        boxShadow: appointmentStatusFilter === status
                          ? '0 4px 12px rgba(212, 165, 116, 0.3)'
                          : 'none'
                      }}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)} ({getFilteredAppointments().filter(a => status === 'all' || a.status === status).length})
                    </button>
                  ))}
                </div>
                
                {getFilteredAppointments().length === 0 ? (
                  <div style={{
                    background: 'white',
                    padding: '3rem',
                    borderRadius: '15px',
                    textAlign: 'center',
                    color: '#5a6c7d'
                  }}>
                    No appointments found for the selected filters
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {getFilteredAppointments().map((appointment) => (
                      <div
                        key={appointment.id}
                        data-testid={`appointment-${appointment.id}`}
                        style={{
                          background: 'white',
                          padding: '1.5rem',
                          borderRadius: '15px',
                          border: '1px solid #f0f0f0'
                        }}
                      >
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr auto',
                          gap: '1.5rem',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.25rem' }}>
                              Customer
                            </div>
                            <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                              {appointment.customer_name}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d' }}>
                              {appointment.customer_email}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d' }}>
                              {appointment.customer_phone}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.25rem' }}>
                              Date & Time
                            </div>
                            <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                              {appointment.appointment_date}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d' }}>
                              {appointment.appointment_time}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.25rem' }}>
                              Total Amount
                            </div>
                            <div style={{ fontWeight: '700', color: '#d4a574', fontSize: '1.25rem' }}>
                              ₹{appointment.total_amount.toFixed(2)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d' }}>
                              {appointment.service_ids.length} service(s)
                            </div>
                          </div>
                          <div>
                            <select
                              value={appointment.status}
                              onChange={(e) => updateAppointmentStatus(appointment.id, e.target.value)}
                              data-testid={`status-${appointment.id}`}
                              style={{
                                padding: '0.5rem 1rem',
                                border: '2px solid #f0f0f0',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                background: appointment.status === 'confirmed' ? 'rgba(34, 197, 94, 0.1)' :
                                          appointment.status === 'completed' ? 'rgba(59, 130, 246, 0.1)' :
                                          appointment.status === 'cancelled' ? 'rgba(239, 68, 68, 0.1)' :
                                          'rgba(251, 191, 36, 0.1)',
                                color: appointment.status === 'confirmed' ? '#16a34a' :
                                       appointment.status === 'completed' ? '#2563eb' :
                                       appointment.status === 'cancelled' ? '#dc2626' :
                                       '#d97706'
                              }}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                        </div>
                        {appointment.notes && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: '#fafafa',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            color: '#5a6c7d'
                          }}>
                            <strong>Notes:</strong> {appointment.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <h2 style={{ fontSize: '1.75rem', color: '#2c3e50', margin: 0 }}>
                    Services ({services.length})
                  </h2>
                  <button
                    onClick={() => setShowAddServiceModal(true)}
                    className="btn-primary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem'
                    }}
                  >
                    <Plus size={20} />
                    Add New Service
                  </button>
                </div>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {services.map((service) => (
                    <div
                      key={service.id}
                      data-testid={`service-admin-${service.id}`}
                      style={{
                        background: 'white',
                        padding: '1.5rem',
                        borderRadius: '15px',
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      {editingService === service.id ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px auto', gap: '1rem', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            style={{
                              padding: '0.5rem',
                              border: '2px solid #f0f0f0',
                              borderRadius: '8px'
                            }}
                          />
                          <input
                            type="text"
                            value={editForm.category}
                            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                            style={{
                              padding: '0.5rem',
                              border: '2px solid #f0f0f0',
                              borderRadius: '8px'
                            }}
                          />
                          <input
                            type="number"
                            value={editForm.price}
                            onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                            style={{
                              padding: '0.5rem',
                              border: '2px solid #f0f0f0',
                              borderRadius: '8px'
                            }}
                          />
                          <input
                            type="number"
                            value={editForm.duration}
                            onChange={(e) => setEditForm({ ...editForm, duration: parseInt(e.target.value) })}
                            style={{
                              padding: '0.5rem',
                              border: '2px solid #f0f0f0',
                              borderRadius: '8px'
                            }}
                          />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={editForm.is_trending}
                              onChange={(e) => setEditForm({ ...editForm, is_trending: e.target.checked })}
                              style={{ cursor: 'pointer' }}
                            />
                            <TrendingUp size={16} color="#f59e0b" />
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleSaveService(service.id)}
                              style={{
                                padding: '0.5rem',
                                background: '#22c55e',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <Save size={18} />
                            </button>
                            <button
                              onClick={() => setEditingService(null)}
                              style={{
                                padding: '0.5rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                          gap: '1.5rem',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <div style={{ fontSize: '0.75rem', color: '#c89b64' }}>
                                {service.category}
                              </div>
                              {service.is_trending && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  padding: '0.25rem 0.5rem',
                                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                  color: 'white',
                                  borderRadius: '50px',
                                  fontSize: '0.7rem',
                                  fontWeight: '600'
                                }}>
                                  <TrendingUp size={12} />
                                  Trending
                                </span>
                              )}
                            </div>
                            <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '1.125rem' }}>
                              {service.name}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.25rem' }}>
                              Price
                            </div>
                            <div style={{ fontWeight: '700', color: '#d4a574', fontSize: '1.125rem' }}>
                              ₹{service.price.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.25rem' }}>
                              Duration
                            </div>
                            <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                              {service.duration} min
                            </div>
                          </div>
                          <div>
                            <div style={{
                              padding: '0.35rem 0.75rem',
                              background: service.is_active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              color: service.is_active ? '#16a34a' : '#dc2626',
                              borderRadius: '50px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textAlign: 'center'
                            }}>
                              {service.is_active ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleEditService(service)}
                              style={{
                                padding: '0.5rem',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              style={{
                                padding: '0.5rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  Reports & Export
                </h2>
                
                <div style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '15px',
                  border: '1px solid #f0f0f0'
                }}>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                    Generate Appointment Report
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Filter Type
                      </label>
                      <select
                        value={reportFilter.type}
                        onChange={(e) => setReportFilter({ ...reportFilter, type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #f0f0f0',
                          borderRadius: '8px'
                        }}
                      >
                        <option value="all">All Time</option>
                        <option value="day">Specific Day</option>
                        <option value="month">Specific Month</option>
                        <option value="year">Specific Year</option>
                        <option value="custom">Custom Range</option>
                      </select>
                    </div>
                    
                    {reportFilter.type !== 'all' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                          {reportFilter.type === 'custom' ? 'Start Date' : 'Date'}
                        </label>
                        <input
                          type="date"
                          value={reportFilter.startDate}
                          onChange={(e) => setReportFilter({ ...reportFilter, startDate: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #f0f0f0',
                            borderRadius: '8px'
                          }}
                        />
                      </div>
                    )}
                    
                    {reportFilter.type === 'custom' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                          End Date
                        </label>
                        <input
                          type="date"
                          value={reportFilter.endDate}
                          onChange={(e) => setReportFilter({ ...reportFilter, endDate: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #f0f0f0',
                            borderRadius: '8px'
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={downloadCSVReport}
                      className="btn-primary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FileDown size={20} />
                      Download CSV Report
                    </button>
                    
                    <button
                      onClick={syncToGoogleSheets}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: 'white',
                        border: '2px solid #d4a574',
                        borderRadius: '50px',
                        color: '#d4a574',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      <Upload size={20} />
                      Sync to Google Sheets
                    </button>
                  </div>
                  
                  <div style={{
                    marginTop: '2rem',
                    padding: '1rem',
                    background: '#fef5e7',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: '#5a6c7d'
                  }}>
                    <strong>Report includes:</strong> Booking ID, Customer Name, Email, Phone, Appointment Date & Time, Services, Total Amount, Status, Notes, and Booking Timestamp
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  Service Usage Analytics
                </h2>
                
                <div style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '15px',
                  border: '1px solid #f0f0f0',
                  marginBottom: '2rem'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Time Period
                      </label>
                      <select
                        value={analyticsFilter.type}
                        onChange={(e) => {
                          setAnalyticsFilter({ ...analyticsFilter, type: e.target.value });
                          if (e.target.value !== 'all') {
                            setTimeout(fetchAnalytics, 100);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #f0f0f0',
                          borderRadius: '8px'
                        }}
                      >
                        <option value="all">All Time</option>
                        <option value="day">Specific Day</option>
                        <option value="month">Specific Month</option>
                        <option value="year">Specific Year</option>
                      </select>
                    </div>
                    
                    {analyticsFilter.type !== 'all' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                          Date
                        </label>
                        <input
                          type="date"
                          value={analyticsFilter.date}
                          onChange={(e) => {
                            setAnalyticsFilter({ ...analyticsFilter, date: e.target.value });
                            setTimeout(fetchAnalytics, 100);
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '2px solid #f0f0f0',
                            borderRadius: '8px'
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  {analytics && (
                    <>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '1rem',
                        marginBottom: '2rem'
                      }}>
                        <div style={{
                          padding: '1.5rem',
                          background: '#fef5e7',
                          borderRadius: '10px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4a574' }}>
                            {analytics.total_appointments}
                          </div>
                          <div style={{ color: '#5a6c7d' }}>Total Appointments</div>
                        </div>
                        <div style={{
                          padding: '1.5rem',
                          background: '#f0fdf4',
                          borderRadius: '10px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#22c55e' }}>
                            {analytics.total_services_used}
                          </div>
                          <div style={{ color: '#5a6c7d' }}>Services Used</div>
                        </div>
                        <div style={{
                          padding: '1.5rem',
                          background: '#fef2f2',
                          borderRadius: '10px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                            {analytics.all_services.filter(s => s.usage_count === 0).length}
                          </div>
                          <div style={{ color: '#5a6c7d' }}>Unused Services</div>
                        </div>
                      </div>
                      
                      {getChartData() && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#2c3e50' }}>
                            Top 10 Most Popular Services
                          </h3>
                          <Bar 
                            data={getChartData()} 
                            options={{
                              responsive: true,
                              plugins: {
                                legend: { display: false }
                              }
                            }}
                          />
                        </div>
                      )}
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div>
                          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#2c3e50' }}>
                            Most Popular Services
                          </h3>
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {analytics.most_popular.slice(0, 10).map((service, index) => (
                              <div
                                key={service.service_id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.75rem',
                                  background: index % 2 === 0 ? '#fafafa' : 'white',
                                  borderRadius: '8px',
                                  marginBottom: '0.5rem'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                    {service.service_name}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#5a6c7d' }}>
                                    {service.category}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: '700', color: '#22c55e' }}>
                                    {service.usage_count} bookings
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#5a6c7d' }}>
                                    ₹{service.revenue.toFixed(2)} revenue
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#2c3e50' }}>
                            Least Used Services
                          </h3>
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {analytics.least_popular.slice(0, 10).map((service, index) => (
                              <div
                                key={service.service_id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.75rem',
                                  background: index % 2 === 0 ? '#fafafa' : 'white',
                                  borderRadius: '8px',
                                  marginBottom: '0.5rem'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                    {service.service_name}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#5a6c7d' }}>
                                    {service.category}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: '700', color: '#ef4444' }}>
                                    {service.usage_count} bookings
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#5a6c7d' }}>
                                    ₹{service.revenue.toFixed(2)} revenue
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  Gallery Management
                </h2>
                
                {/* Image Upload Component */}
                <ImageUpload onUploadSuccess={fetchData} />
                
                {/* Gallery Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {galleryImages.map((image, index) => (
                    <div
                      key={image.id}
                      style={{
                        background: 'white',
                        borderRadius: '15px',
                        overflow: 'hidden',
                        border: '1px solid #f0f0f0',
                        position: 'relative'
                      }}
                    >
                      <img
                        src={image.image_url}
                        alt={image.title}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                          {image.title || 'Untitled'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#5a6c7d', marginBottom: '0.5rem' }}>
                          {image.category} • Order: {image.order}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleUpdateImageOrder(image.id, image.order - 1)}
                            disabled={index === 0}
                            style={{
                              padding: '0.5rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              opacity: index === 0 ? 0.5 : 1,
                              flex: 1
                            }}
                          >
                            <MoveUp size={16} />
                          </button>
                          <button
                            onClick={() => handleUpdateImageOrder(image.id, image.order + 1)}
                            disabled={index === galleryImages.length - 1}
                            style={{
                              padding: '0.5rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: index === galleryImages.length - 1 ? 'not-allowed' : 'pointer',
                              opacity: index === galleryImages.length - 1 ? 0.5 : 1,
                              flex: 1
                            }}
                          >
                            <MoveDown size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteGalleryImage(image.id)}
                            style={{
                              padding: '0.5rem',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              flex: 1
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && userRole === 'primary' && (
              <div>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                  User Management
                </h2>
                <UserManagement />
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Password Change Modal */}
      <AdminPasswordChange 
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
      
      {/* Add Service Modal */}
      <AddServiceForm
        isOpen={showAddServiceModal}
        onClose={() => setShowAddServiceModal(false)}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default AdminDashboard;
